//  https://thescottyjam.github.io/snap.js/#!/utils

import type { IsoDate, IsoDateTime, IsoIshDateTime } from '@/common/common-models'

/**
 *
 * @param objectType
 */
export function simpleClone<ObjectType>(objectType: ObjectType): ObjectType {
  return JSON.parse(JSON.stringify(objectType))
}

/**
 *  N.B. very, very simple comparison. Prop order is required.
 * @param objectType
 */
export function isEqual<ObjectType>(objectOne: ObjectType, objectTwo: ObjectType): boolean {
  return JSON.stringify(objectOne) === JSON.stringify(objectTwo)
}

/**
 * Handles sorting on strings or numbers
 * @param prop
 * @param someArray
 */
export function sortArray<ObjectType, PropName extends keyof ObjectType>(
  prop: ((t: ObjectType) => string | number) | PropName,
  someArray: ObjectType[],
  order: 'ASC' | 'DESC' = 'ASC'
): ObjectType[] {
  if (!someArray || someArray.length === 0) {
    return someArray
  }

  return simpleClone(someArray).sort((a, b) => {
    const propValueA: unknown = typeof prop === 'function' ? prop(a) : a[prop]
    const propValueB: unknown = typeof prop === 'function' ? prop(b) : b[prop]

    if (typeof propValueA === 'string' && typeof propValueB === 'string') {
      const compA: string = (order === 'ASC' ? propValueA : propValueB).toUpperCase()
      const compB: string = (order === 'ASC' ? propValueB : propValueA).toUpperCase()
      // const compB: string = propValueB.toString().toUpperCase();

      if (compA < compB) {
        return -1
      }
      if (compA > compB) {
        return 1
      }
      // names must be equal
      return 0
    }

    if (typeof propValueA === 'number' && typeof propValueB === 'number') {
      return order === 'ASC' ? propValueA - propValueB : propValueB - propValueA
    }

    return 0
  })
}

/**
 * N.B. it will return 5.12.3 as FALSE, which is true.
 * @param str
 */
export function isNumeric(str: string | number) {
  if (typeof str === 'number') {
    return true
  }
  if (typeof str !== 'string') return false // we only process strings from here on!
  return (
    !isNaN(str as any as number) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ) // ...and ensure strings of whitespace fail
}

export function hasAtLeastOneCharacter(
  someNumber: string | number,
  sepsAllowed?: string[]
): boolean {
  let valueToTest = someNumber.toString()
  if (sepsAllowed) {
    valueToTest = sepsAllowed.reduce((accum, sep) => {
      accum = accum.replace(sep, '')
      return accum
    }, valueToTest)
  }
  const regex = /[a-zA-Z]/
  return regex.test(valueToTest)
}

/**
 * Use this if there is a one to one mapping.   E.g.
 * {
 *      "MA": {city: "Boston"},
 *      "CT": {"city": "New York"}
 * }
 * @param prop
 * @param someArray
 */
// public convertArrayToObject<T>(prop: string, someArray: T[]): IObjectKeyType<T> {
//     if (R.isNil(someArray) || someArray.length === 0) {
//         return {};
//     }
//     // @ts-ignore
//     const objFromListWith = R.curry((fn, list) => R.chain(R.zipObj, R.map(fn))(list));
//     const result = objFromListWith(
//         R.prop(prop)
//     )(someArray);
//     // @ts-ignore
//     return result;
// }
export function convertArrayToObject<T>(
  prop: ((t: T) => any) | string,
  someArray: T[]
): Record<string, T> {
  if (isNil(someArray) || someArray.length === 0) {
    return {}
  }
  return someArray.reduce(
    (accum, obj: T) => {
      // @ts-ignore
      const propValueString = typeof prop === 'string' ? obj[prop] : prop(obj)
      //  @ts-ignore
      accum[propValueString] = obj
      return accum
    },
    {} as Record<string, T>
  )
}

/**
 * Use this if there is a one to many mapping.   E.g.
 * {
 *      "MA": [
 *          {city: "Boston"},
 *          {city: "Winchester"}
 *      ],
 *      "CT": [
 *          {city: "New Canaan"}
 *      ]
 * }
 * @param prop
 * @param someArray
 */
export function convertArrayToObjectArray<T>(
  prop: ((t: T) => any) | keyof T,
  someArray: T[]
): Record<string, T[]> {
  return someArray.reduce<Record<string, T[]>>((accum, obj: T) => {
    // @ts-ignore
    const propValueString = typeof prop === 'string' ? obj[prop] : prop(obj)
    //  Produce a keyed object
    if (!accum[propValueString]) {
      accum[propValueString] = []
    }
    accum[propValueString].push(obj)
    return accum
  }, {})
}

export function unique<T>(objs: T[]): T[] {
  // return [...new Set(objs)];
  // return Array.from(new Set(simpleClone(objs)));
  return Array.from(
    new Set(
      objs.map((obj) => {
        return JSON.stringify(obj)
      })
    )
  ).map((obj) => {
    return JSON.parse(obj)
  })
  // return R.uniq(objs);
}

export function uniqueBy<InputObject>(
  objs: InputObject[],
  propNameOrFunction: ((t: InputObject) => string) | keyof InputObject
): InputObject[] {
  const seen = new Set<string>()
  const result: InputObject[] = []
  for (const obj of objs) {
    let pValue: string = ''
    if (typeof propNameOrFunction === 'string') {
      pValue = obj[propNameOrFunction] as any as string
    } else {
      //  @ts-ignore
      pValue = propNameOrFunction(obj) as any as string
    }
    if (!seen.has(pValue)) {
      seen.add(pValue)
      result.push(obj)
    }
  }
  return result
}

/*
export function unique<T>(objs: T[]): T[] {
  // // @ts-ignore
  // return [...new Set(objs)];
  return R.uniq(objs);
}
*/

export function pluckUnique<InputObject, OutputObject>(
  prop: ((t: InputObject) => OutputObject) | keyof InputObject,
  someArray: InputObject[]
): OutputObject[] {
  return unique(pluck(prop, someArray))
}

/**
 * Removes duplicate objects from an array by comparing their complete structure.
 * Objects are considered duplicates if they have exactly the same properties and values.
 * This is useful for deduplicating results where athletes might have multiple identical entries.
 *
 * @param objects Array of objects to deduplicate
 * @returns Array with duplicate objects removed (first occurrence is kept)
 */
export function uniqueObjects<T>(objects: T[]): T[] {
  return unique(objects)
}

/**
 * Filters an array of IndividualResult objects to keep only unique results by athleteId.
 * When duplicate athleteIds are found, the first occurrence is kept.
 * This is commonly used for overall results where an athlete might appear in multiple heats.
 *
 * @param results Array of IndividualResult objects
 * @returns Array of unique IndividualResult objects filtered by athleteId
 */
export function uniqueResultsByAthleteId<T extends { athleteId: number | string }>(results: T[]): T[] {
  return uniqueBy(results, (result) => result.athleteId.toString())
}

/**
 * Pluck a property from an array of objects.
 * @param propNameOrFunction
 * @param someArray
 */
export function pluck<InputObject, OutputObject>(
  propNameOrFunction: ((t: InputObject) => OutputObject) | keyof InputObject,
  someArray: InputObject[]
): OutputObject[] {
  return someArray.reduce<OutputObject[]>((accum, obj: InputObject) => {
    let pValue
    if (typeof propNameOrFunction === 'string') {
      pValue = obj[propNameOrFunction]
    } else {
      //  @ts-ignore
      pValue = propNameOrFunction(obj)
    }

    // const pValue =
    //   typeof propNameOrFunction === "string"
    //     ? obj[propNameOrFunction]
    //     : propNameOrFunction(obj);
    accum.push(pValue)
    return accum
  }, [])
}

export function propValue<T, K extends keyof T>(obj: T, key: K) {
  return obj[key]
}

export function intersection<T>(array1: T[], array2: T[]) {
  return array1.filter((x) => array2.includes(x))
}

export function isOnlyNumbers(someValue: string): boolean {
  return /^\d+$/.test(someValue)
}

export async function executePromisesSequentially(proms: (() => Promise<unknown>)[]) {
  const tasks = proms
  for (const fn of tasks) {
    await fn()
  }
}

export function isValidHttpUrl(someUrl: string) {
  let url

  try {
    url = new URL(someUrl)
  } catch (_) {
    return false
  }

  return url.protocol === 'http:' || url.protocol === 'https:'
}

export interface IConvertedSecondsToHMS {
  hours: number
  minutes: number
  seconds: number
  hundredths: number
}

export function convertSecondsToHMS(sec: number): IConvertedSecondsToHMS {
  const hours = Math.floor(sec / 3600)
  const minutes = Math.floor((sec - hours * 3600) / 60)
  const seconds = Math.floor(sec - hours * 3600 - minutes * 60)
  const hundredths = getFractionalPartAsWholeNumber(sec)
  return { hours, minutes, seconds, hundredths }
}

export function convertSecondsToUserDisplayString(inputSeconds: number): string {
  return getUserOutputText(convertSecondsToHMS(inputSeconds))
}

/**
 * Convert IConvertedSecondsToHMS to seconds
 * @param convertedSecondsToHMS
 */
export function convertedSecondsToHMSToSeconds(
  convertedSecondsToHMS: IConvertedSecondsToHMS
): number {
  const { hours, minutes, seconds, hundredths } = convertedSecondsToHMS
  return hours * 3600 + minutes * 60 + seconds + hundredths / 100
}

export function getUserOutputText(convertedSecondsToHMS: IConvertedSecondsToHMS): string {
  const keys: (keyof IConvertedSecondsToHMS)[] = ['hours', 'minutes', 'seconds', 'hundredths']
  const res: string = keys.reduce<string>((accum, key) => {
    const value = convertedSecondsToHMS[key]
    if (accum.length > 0) {
      accum += key === 'hundredths' ? '.' : ':'
    }
    if (key === 'hours' && value === 0) {
      return accum
    }
    if (key === 'minutes' && value === 0 && convertedSecondsToHMS.hours === 0) {
      //  E.g. the 100m, if hours and mins are 0, don't show the hours, minutes.
      return accum
    }
    accum += value.toString().padStart(2, '0')
    return accum
  }, '')

  return res
}

/**
 * Given a float as input, return the fractional part of the number as a whole number.
 */
export function getFractionalPartAsWholeNumber(
  value: number,
  decimalPlaces: 1 | 2 | 3 = 2
): number {
  let multiplier = 100
  if (decimalPlaces === 1) {
    multiplier = 10
  }
  if (decimalPlaces === 3) {
    multiplier = 1000
  }

  return Math.round((value % 1) * multiplier)
}

/**
 * Given a number, return the number as a string with leading zeros as specified by the number of digits.
 * @param value
 * @param digits
 * @returns E.g. getNumberWithLeadingZeros(1, 2) returns "01", getNumberWithLeadingZeros(1, 3) returns "001"
 */
export function getNumberWithLeadingZeros(value: number, digits: 1 | 2 | 3 = 2): string {
  const multiplier = 10 ** digits
  const valueWithLeadingZeros = (multiplier + value).toString().slice(1)
  return valueWithLeadingZeros
}

export type ObjectWidthAndHeight = { width: number; height: number }
export function getElementWidthAndHeight(selector: string): ObjectWidthAndHeight {
  const element: HTMLElement | null = document.querySelector(selector)
  if (element) {
    return { width: element.offsetWidth, height: element.offsetHeight }
  }
  return { width: -1, height: -1 }
}

export function convertObjectToUrlParams(obj: unknown): string {
  // return Object.entries(obj)
  //   .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
  //   .join("&");

  return Object.entries(obj as Record<string, string | number | boolean>)
    .map((kv) => kv.map(encodeURIComponent).join('='))
    .join('&')
}

export function isNil(value: any): boolean {
  // check for null or undefined or empty string, array, object or number without using Ramda
  // return value == null || value === "" || value.length === 0;
  return value == null || typeof value === 'undefined' || value === '' || value.length === 0
}

// given an iso date in format 2021-01-01T00:00:00+01:00, return the date in format 1st Jan 2021
export function getFormattedDate(date: string): string {
  const formattedDate = new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
  return formattedDate
}

// given an iso date in format 2021-01-01T00:00:00+01:00, return the time in 23rd Jan 2021
export function formatDateWithSuffix(isoDate: IsoDate) {
  try {
    // Parse the ISO date string
    const dateObj = new Date(isoDate)
    const day = dateObj.getDate()
    const month = dateObj.toLocaleString('default', { month: 'short' })
    const year = dateObj.getFullYear()

    // Determine the suffix for the day
    let suffix
    if (day >= 11 && day <= 13) {
      suffix = 'th'
    } else {
      switch (day % 10) {
        case 1:
          suffix = 'st'
          break
        case 2:
          suffix = 'nd'
          break
        case 3:
          suffix = 'rd'
          break
        default:
          suffix = 'th'
      }
    }

    // Format the date
    const formattedDate = `${day}${suffix} ${month} ${year}`
    return formattedDate
  } catch (error) {
    return 'Invalid date format. Please provide a valid ISO date (yyyy-mm-dd).'
  }
}

// given an iso date in format 2021-01-01T00:00:00+01:00, return the time in 23rd Jan 2021
export function formatDateTime(
  isoDate: IsoDateTime,
  parts: 'BOTH' | 'DATE' | 'TIME',
  options?: Intl.DateTimeFormatOptions
) {
  try {
    const optionsInternal: Intl.DateTimeFormatOptions = { timeZone: 'Greenwich' }
    if (parts === 'BOTH') {
      optionsInternal.dateStyle = 'medium'
      optionsInternal.timeStyle = 'short'
    }
    if (parts === 'DATE') {
      optionsInternal.dateStyle = 'medium'
    }
    if (parts === 'TIME') {
      optionsInternal.timeStyle = 'short'
    }

    // Parse the ISO date string
    const dateObj = new Date(isoDate)
    return new Intl.DateTimeFormat('en-GB', options ? options : optionsInternal).format(dateObj)
  } catch (error) {
    return 'Invalid date format. Please provide a valid ISO date (yyyy-mm-dd).'
  }
}

export function getDatePartFromIsoString(isoString: IsoDateTime | IsoIshDateTime): IsoDate {
  isoString = isoString.replace(' ', 'T')
  return isoString.split('T')[0]
}

export function fixDateIsoIsh(isoString: IsoDateTime | IsoIshDateTime): IsoDateTime {
  return isoString.replace(' ', 'T')
}

//  Function to create a guid
export function createGuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// Function that accepts array of IsoDate an optionally an IsoDate that is a point in time.
// It returns the next date in the sequence, if the point in time is before the first in sequence, then the
// first date in the sequence is returned.  If the point in time is after the last date in the sequence, then
// the last date in the sequence is returned, else the first date where the point in time is equal to or after the point in time.
export function getNextDateInSequence(dates: IsoDate[], dateToMatch?: IsoDate): IsoDate {
  const dateToMatchFNS = dateToMatch ? dateToMatch : new Date().toISOString().split('T')[0]
  const dateToMatchDateOnly = dateToMatchFNS.split('T')[0]

  if (dates.length === 0) {
    return ''
  }

  dates = dates.map((date) => date.split('T')[0]).sort()

  if (dates.length === 1) {
    return dates[0]
  }

  if (dateToMatchDateOnly < dates[0]) {
    return dates[0]
  }
  const lastDateInArray = dates[dates.length - 1]
  if (dateToMatchDateOnly > lastDateInArray) {
    return lastDateInArray
  }

  const debug = {
    dateToMatchDateOnly,
    dates,
    junk: [] as any,
    accum: 'zzz'
  }

  const res = dates.reduce((accum, dateOnly, index) => {
    if (dateToMatchDateOnly === dateOnly) {
      debug.junk.push(
        dateOnly + '===' + dateToMatchDateOnly + ' ? ' + (dateOnly === dateToMatchDateOnly)
      )
      accum = dateOnly
    }
    if (accum === '') {
      if (dateToMatchDateOnly <= dateOnly) {
        return dateOnly
      }
      // is index last element
      if (index === dates.length - 1) {
        return dateOnly
      }
    }

    return accum
  }, '')

  // debug.accum = res
  return res
}

/*
given an iso time, e.g. 2025-06-08T12:40:00.000Z or "2025-06-08T12:40:00+01:00", return the time part only.
as a string in format HH:mm
But if the time is UTC time ensure that it's local time that's returned.
Use temporal time if possible
 */
export function getTimePartFromIsoString(isoString: IsoDateTime): string {
  let timePart

  // if does not contain a T, then return empty string
  if (isoString.indexOf('T') === -1) {
    return ''
  }

  if (isUtcTime(isoString)) {
    // Create date object - this automatically converts to local time
    const dateObj = new Date(isoString)

    // Format with built-in methods that respect local timezone including DST
    timePart = dateObj.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
  } else {
    timePart = isoString.split('T')[1].split('.')[0]

    // take first 5 characters
    timePart = timePart.substring(0, 5)
  }

  if (timePart === 'Invalid Date') {
    return ''
  }
  if (timePart === '00:00') {
    return 'TBC'
  }
  return timePart
}

// Create function that takes in an ISO string and checks if it's UTC time.
export function isUtcTime(isoString: IsoDateTime): boolean {
  return isoString.endsWith('Z')
}
